'use client';

import type { ReactNode } from 'react';
import Image from 'next/image';

import { useTranslation } from 'react-i18next';

import type { LandingNav, LandingTheme } from '../../_lib/schema/landing-config.schema';
import { contrastForeground, landingThemeStyle } from './landing-design';
import { LandingFooterLink } from './landing-footer-link';
import { pickLocalized } from './landing-context';
import styles from './studio-site-footer.module.css';

export function StudioSiteFooter({ nav, businessName, logoUrl, locale, fontFamily, legalControls, disableNavigation, theme }: {
  nav: LandingNav;
  businessName?: string | null;
  logoUrl?: string | null;
  locale?: string;
  fontFamily?: LandingTheme['fontFamily'];
  theme?: LandingTheme;
  legalControls?: ReactNode;
  disableNavigation?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const arabic = language.startsWith('ar');
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, language);
  const footer = nav.footer;
  const translate = (key: string) => t(key, { lng: language });
  const themeStyle = landingThemeStyle({ ...theme, fontFamily: fontFamily ?? theme?.fontFamily });
  return <footer className={styles.footer} dir={arabic ? 'rtl' : undefined} style={{ ...themeStyle, backgroundColor: footer?.backgroundColor ?? themeStyle.backgroundColor, color: footer?.textColor ?? (footer?.backgroundColor ? contrastForeground(footer.backgroundColor) : themeStyle.color) }} data-test="studio-site-footer">
    <div className={styles.columns}>
      <div>
        {footer?.showLogo !== false ? logoUrl ? <div className={styles.logoSurface}><Image className={styles.logo} src={logoUrl} alt={nav.logo?.alt ?? businessName ?? ''} width={220} height={72} unoptimized /></div> : <h2>{businessName}</h2> : null}
        {footer?.showDescription !== false ? <p>{local(nav.brand?.footerText, nav.brand?.footerText_ar)}</p> : null}
      </div>
      {footer?.showNavigation !== false ? <nav aria-label={arabic ? 'روابط سريعة' : 'Quick links'}>
        <h2>{local(footer?.linksHeading, footer?.linksHeading_ar) ?? (arabic ? 'روابط سريعة' : 'Quick Links')}</h2>
        {(nav.footerLinks ?? nav.links).map((link) => <LandingFooterLink href={link.href} key={link.href} disabled={disableNavigation}>{local(link.label, link.label_ar)}</LandingFooterLink>)}
      </nav> : null}
    </div>
    <div className={styles.bottom}>
      <span>{local(nav.brand?.copyright, nav.brand?.copyright_ar) ?? `© ${new Date().getFullYear()} ${businessName ?? ''}`}</span>
      <nav aria-label={translate('marketing:legal.label')}><LandingFooterLink href="/privacy-policy" disabled={disableNavigation}>{translate('marketing:privacyPolicy')}</LandingFooterLink><LandingFooterLink href="/terms-of-service" disabled={disableNavigation}>{translate('marketing:termsOfService')}</LandingFooterLink><LandingFooterLink href="/cookie-policy" disabled={disableNavigation}>{translate('marketing:cookiePolicy')}</LandingFooterLink>{legalControls}</nav>
      <span>{translate('marketing:footer.poweredBy')}</span>
    </div>
  </footer>;
}
